import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { parse, ParseResult, unparse } from 'papaparse';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MigrationsCiscoSiteManagementContext } from './migrations-cisco-site-management.context';
import { CiscoMigrationSite } from '../../../shared/models/generated/ziroModels';
import {
  DatatableColumn,
  DatatableOnRowFieldChange,
  DatatableRow,
} from '../../../shared/tables/datatable/datatable.models';
import { DatatableComponent } from '../../../shared/tables/datatable/datatable.component';
import { DatatableFieldTypes } from '../../../shared/tables/datatable/datatable-field.models';
import { ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import {
  BottomNavClearButtonsList,
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ButtonSizes, ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import {
  MigrationsCiscoSourceFieldMappingComponent,
  SourceFieldMappingBottomNavState,
} from '../migrations-cisco-source-field-mapping/migrations-cisco-source-field-mapping.component';
import { COUNTRY_NAME_BY_CODE, COUNTRY_OPTIONS } from '../../../shared/country-codes/country-codes.constants';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { MigrationsAssessmentsContext } from '../../services/migrations-assessments.context';

interface SiteRow extends DatatableRow {
  rowId: string;
  siteId: string;
  name: string;
  country: string;
}

interface SiteCsvRow {
  Name?: string;
  'Country Code'?: string;
}

const CSV_HEADERS: string[] = ['Name', 'Country Code'];

export enum MigrationsCiscoSiteManagementTabs {
  SITES = 'sites',
  SOURCE_FIELD_MAPPING = 'sourceFieldMapping',
}

@Component({
  selector: 'ziro-migrations-cisco-site-management',
  templateUrl: './migrations-cisco-site-management.component.html',
  standalone: false,
  styleUrls: ['../../migrations.component.scss', './migrations-cisco-site-management.component.scss'],
  providers: [MigrationsCiscoSiteManagementContext, MigrationsAssessmentsContext],
})
export class MigrationsCiscoSiteManagementComponent implements OnInit, OnDestroy {
  private _context = inject(MigrationsCiscoSiteManagementContext);
  private _bottomNavService = inject(BottomNavService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _toastService = inject(ToastService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _domSanitizer = inject(DomSanitizer);
  private _ziroFormStateService = inject(ZiroFormStateService);
  private _translateService = inject(TranslateService);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);

  @ViewChild(DatatableComponent) private _datatable: DatatableComponent<SiteRow>;
  @ViewChild(MigrationsCiscoSourceFieldMappingComponent)
  private _sourceFieldMappingComponent: MigrationsCiscoSourceFieldMappingComponent;

  isLoading = true;
  isSaving = false;
  rows: SiteRow[] = [];
  templateUrl: string;
  hasSites = false;
  activeTab: MigrationsCiscoSiteManagementTabs = MigrationsCiscoSiteManagementTabs.SITES;

  readonly buttonSizes = ButtonSizes;
  readonly buttonStyles = ButtonStyles;
  readonly buttonTypes = ButtonTypes;
  readonly icons = ZiroIcons;
  readonly tabs = MigrationsCiscoSiteManagementTabs;

  columns: DatatableColumn<SiteRow>[] = [
    {
      name: 'name',
      label: 'tkey;migrations.cisco.site_management.table.name.header',
      columnSize: 'col-sm-6',
      columnCharacterWidth: 45,
    },
    {
      name: 'country',
      label: 'tkey;migrations.cisco.site_management.table.country.header',
      columnSize: 'col-sm-4',
      columnCharacterWidth: 30,
      // country can be null for newly added/imported rows, so type can't be inferred from the first row's value
      sortFn: (a: string | null, b: string | null) => (a ?? '').localeCompare(b ?? ''),
    },
  ];

  private _nextTempId = 0;
  private _subscriptions = new Subscription();
  private _tenantId: string;
  private _assessmentUuid: string;

  ngOnInit(): void {
    this._tenantId = this._route.snapshot.paramMap.get('tenantId');
    this._assessmentUuid = this._route.snapshot.paramMap.get('id');
    this._initBottomNav();
    this.templateUrl = this._getTemplateUrl() as string;

    const stateSub = this._context.state$.subscribe((sites: CiscoMigrationSite[]) => {
      this._buildRows(sites);
      this.hasSites = (sites ?? []).length > 0;
      this.isLoading = false;
    });
    this._subscriptions.add(stateSub);

    const breadcrumbSub = this._migrationsAssessmentsContext.state$.subscribe((migrations) => {
      const assessment = migrations.ciscoPbxExports.find((migration) => migration.id === this._assessmentUuid);
      this._initBreadcrumbs(assessment?.name);
    });
    this._subscriptions.add(breadcrumbSub);
  }

  ngOnDestroy(): void {
    this._breadcrumbsService.clearBreadcrumbs();
    this._bottomNavService.dispatch(new BottomNavClearButtonsList());
    this._ziroFormStateService.setIsFormDirty(false);
    this._subscriptions.unsubscribe();
  }

  matchesCountryNameFilter = (searchFilter: string, row: SiteRow): boolean =>
    (COUNTRY_NAME_BY_CODE[row.country] ?? '').toLowerCase().includes(searchFilter.toLowerCase().trim());

  onRowFieldChange(event: DatatableOnRowFieldChange<SiteRow>): void {
    this._markDirty();

    if (event.columnId === 'name') {
      event.row.name = (event.newValue ?? '').trim();
    }
  }

  onAddSite(): void {
    this.rows = [
      {
        rowId: `new-${this._nextTempId++}`,
        siteId: null,
        name: '',
        country: null,
        fields: {
          name: {
            type: DatatableFieldTypes.TEXT,
            value: '',
            maxLength: 50,
            ...this._nameFieldConfig(),
          },
          country: {
            type: DatatableFieldTypes.SELECT,
            value: null,
            isClearable: true,
            options: COUNTRY_OPTIONS,
            bindValue: 'value',
            bindLabel: 'label',
          },
        },
        actions: this._deleteActions(),
      },
      ...this.rows,
    ];

    this._markDirty();
  }

  onDeleteSite(row: SiteRow): void {
    this.rows = this.rows.filter((r) => r.rowId !== row.rowId);
    this._markDirty();
  }

  onImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0);
    input.value = '';

    if (file) {
      this._importCsv(file);
    }
  }

  onNavChange(event: NgbNavChangeEvent<MigrationsCiscoSiteManagementTabs>): void {
    if (event.nextId === MigrationsCiscoSiteManagementTabs.SITES) {
      this._initBottomNav();
    } else {
      this._initSourceFieldMappingBottomNav();
    }
  }

  onMappingsBottomNavStateChange(state: SourceFieldMappingBottomNavState): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'source-field-mapping-cancel',
        state: { buttonDisableState: { disabled: state.cancelDisabled, tooltipKey: '' } },
      }),
    );
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'source-field-mapping-save',
        state: {
          pending: state.savePending,
          buttonDisableState: { disabled: state.saveDisabled, tooltipKey: state.saveDisabledTooltipKey },
          tooltipVisible: !!state.saveDisabledTooltipKey,
        },
      }),
    );
  }

  private _buildRows(sites: CiscoMigrationSite[]): void {
    this.rows = (sites ?? []).map((site) => this._toRow(site));
  }

  private _toRow(site: CiscoMigrationSite): SiteRow {
    return {
      rowId: site.siteId,
      siteId: site.siteId,
      name: site.name,
      country: site.country,
      fields: {
        name: {
          type: DatatableFieldTypes.TEXT,
          value: site.name,
          maxLength: 50,
          ...this._nameFieldConfig(),
        },
        country: {
          type: DatatableFieldTypes.SELECT,
          value: site.country,
          isClearable: true,
          options: COUNTRY_OPTIONS,
          bindValue: 'value',
          bindLabel: 'label',
        },
      },
      actions: this._deleteActions(),
    };
  }

  private _deleteActions(): SiteRow['actions'] {
    return [
      {
        type: ButtonTypes.BUTTON,
        style: ButtonStyles.OUTLINE_DANGER,
        icon: ZiroIcons.DELETE_SMALL,
        dataAutomation: 'site-management-delete-site',
        onClick: (row: SiteRow) => this.onDeleteSite(row),
      },
    ];
  }

  private _nameFieldConfig(): {
    validation: {
      validator: (value: string) => ZiroFormsValidationState;
      message: () => string;
    }[];
    isUniqueWithMessage: string;
  } {
    return {
      validation: [
        {
          validator: (value: string): ZiroFormsValidationState =>
            value?.trim() ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
          message: (): string => 'tkey;validators.global.required.error',
        },
      ],
      isUniqueWithMessage: 'tkey;migrations.cisco.site_management.table.name.uniqueness.validation.message',
    };
  }

  private _markDirty(): void {
    this._ziroFormStateService.setIsFormDirty(true);
  }

  private _initBreadcrumbs(assessmentName: string): void {
    this._breadcrumbsService.updateBreadcrumbs([
      { label: 'tkey;migrations.title', routerLink: true, url: `/migrations/tenants/${this._tenantId}` },
      {
        label: this._translateService.instant('tkey;migrations.number_report.dashboard.breadcrumb', {
          name: assessmentName,
        }),
        routerLink: true,
        url: `/migrations/tenants/${this._tenantId}/cisco/${this._assessmentUuid}`,
      },
      { label: 'tkey;migrations.cisco.site_management.title' },
    ]);
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'site-management-cancel',
          label: 'tkey;global.button.cancel.text',
          buttonClass: ButtonStyles.DEFAULT,
          dataAutomation: 'site-management-cancel-button',
          cb: () => this._onCancel(),
        },
        {
          id: 'site-management-save',
          label: 'tkey;global.button.save.text',
          icon: ZiroIcons.SAVE_SUCCESS,
          buttonClass: ButtonStyles.PRIMARY,
          dataAutomation: 'site-management-save-button',
          state: { pending: false, buttonDisableState: { disabled: false, tooltipKey: '' } },
          cb: () => this._save(),
        },
      ]),
    );
  }

  private _initSourceFieldMappingBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'source-field-mapping-cancel',
          label: 'tkey;global.button.cancel.text',
          buttonClass: ButtonStyles.DEFAULT,
          dataAutomation: 'source-field-mapping-cancel-mappings-button',
          state: { buttonDisableState: { disabled: false, tooltipKey: '' } },
          cb: () => this._onCancel(),
        },
        {
          id: 'source-field-mapping-save',
          label: 'tkey;migrations.cisco.site_management.source_field_mapping.save_mappings',
          icon: ZiroIcons.SAVE_SUCCESS,
          buttonClass: ButtonStyles.PRIMARY,
          dataAutomation: 'source-field-mapping-save-mappings-button',
          state: { pending: false, buttonDisableState: { disabled: true, tooltipKey: '' } },
          cb: () => this._sourceFieldMappingComponent?.onSaveMappings(),
        },
      ]),
    );

    // the mapping component is created eagerly (destroyOnHide is false) and may already have
    // pending unsaved changes from a prior visit to this tab - sync the buttons to its current state
    if (this._sourceFieldMappingComponent) {
      this.onMappingsBottomNavStateChange(this._sourceFieldMappingComponent.getBottomNavState());
    }
  }

  private _getTemplateUrl(): SafeUrl {
    const csvData = unparse({ fields: CSV_HEADERS, data: [] });
    return this._domSanitizer.bypassSecurityTrustUrl(`data:text/plain;charset=utf8,${encodeURIComponent(csvData)}`);
  }

  private _onCancel(): void {
    this._ziroFormStateService.setIsFormDirty(false);
    this._router.navigate(['../'], { relativeTo: this._route });
  }

  private _setSaveButtonPendingState(pending: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'site-management-save',
        state: { pending, buttonDisableState: { disabled: pending, tooltipKey: '' } },
      }),
    );
  }

  private _save(): void {
    const hasErrors = this.rows.some((row) => this._datatable?.isErrorPresentOnRow(row));
    this._bottomNavService.dispatch(new BottomNavUpdateState({ hasValidationError: hasErrors }));

    if (hasErrors) {
      return;
    }

    const sites: CiscoMigrationSite[] = this.rows.map((row) => ({
      siteId: row.siteId,
      name: row.name.trim(),
      country: row.country || null,
    }));

    this.isSaving = true;
    this._setSaveButtonPendingState(true);

    this._context
      .save(sites)
      .pipe(
        tap(() => {
          this._ziroFormStateService.setIsFormDirty(false);
          this._toastService.push(
            ToastTypes.SUCCESS,
            ZiroIcons.SAVE_SUCCESS,
            'tkey;shared.toast.save.success.title',
            'tkey;migrations.cisco.site_management.toast.save.message',
          );
          this.isSaving = false;
          this._setSaveButtonPendingState(false);
        }),
      )
      .subscribe();
  }

  private _importCsv(file: File): void {
    if (!file) {
      return;
    }

    parse<SiteCsvRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result: ParseResult<SiteCsvRow>) => {
        const headers = result.meta.fields ?? [];
        const errors: string[] = [];

        if (!result.data.length) {
          errors.push('tkey;migrations.cisco.site_management.csv_upload.error.empty_file');
        }
        if (!headers.includes('Name')) {
          errors.push('tkey;migrations.cisco.site_management.csv_upload.error.missing_name_column');
        }

        if (errors.length) {
          const translatedErrors = errors.map((error) => this._translateService.instant(error));
          this._toastService.push(
            ToastTypes.ERROR,
            ZiroIcons.EXCLAMATION,
            'tkey;migrations.cisco.site_management.csv_upload.toast.error.title',
            translatedErrors.join(' '),
          );
          return;
        }

        this._stageImportedRows(result.data);
      },
      error: () => {
        this._toastService.push(
          ToastTypes.ERROR,
          ZiroIcons.EXCLAMATION,
          'tkey;migrations.cisco.site_management.csv_upload.toast.error.title',
          'tkey;migrations.cisco.site_management.csv_upload.error.parse_error',
        );
      },
    });
  }

  private _stageImportedRows(csvRows: SiteCsvRow[]): void {
    const validCountryCodes = new Set(Object.keys(COUNTRY_NAME_BY_CODE));

    const importedRows: SiteRow[] = csvRows.map((csvRow): SiteRow => {
      const name = csvRow['Name']?.trim() ?? '';
      const rawCountry = csvRow['Country Code']?.trim() ?? '';
      const normalizedCountry = rawCountry.toUpperCase() || null;
      const isCountryValid = !normalizedCountry || validCountryCodes.has(normalizedCountry);
      const countryValue = isCountryValid ? normalizedCountry : rawCountry;
      // when the imported code isn't a recognized ISO alpha-2 code, add it as a temporary
      // option so the raw value remains visible/selected in the dropdown while flagged invalid
      const countryOptions = isCountryValid
        ? COUNTRY_OPTIONS
        : [...COUNTRY_OPTIONS, { label: rawCountry, value: rawCountry }];

      return {
        rowId: `imported-${this._nextTempId++}`,
        siteId: null,
        name,
        country: countryValue,
        fields: {
          name: {
            type: DatatableFieldTypes.TEXT,
            value: name,
            maxLength: 50,
            ...this._nameFieldConfig(),
          },
          country: {
            type: DatatableFieldTypes.SELECT,
            value: countryValue,
            isClearable: true,
            options: countryOptions,
            bindValue: 'value',
            bindLabel: 'label',
            validation: isCountryValid
              ? []
              : [
                  {
                    validator: (value: string): ZiroFormsValidationState =>
                      value === rawCountry ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID,
                    message: (): string =>
                      'tkey;migrations.cisco.site_management.table.country.validation.invalid_country_code',
                  },
                ],
          },
        },
        actions: this._deleteActions(),
      };
    });

    this.rows = [...importedRows, ...this.rows];
    this._markDirty();
  }
}
